import React, {useEffect, useState} from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {Checkbox} from "@material-ui/core";
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import EnhancedTable from '../enhanced-table';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';
import { AlertDialog, DraggableDialog, PromptDialog } from '../dialogs';
import { PythonExecutable } from '../executable';
import { app, csv, ipcRenderer} from '../electron-modules';
import {saveMatrixFile} from '../camera-utils';
import { useSnackbar } from 'notistack';
import { Box, DialogContentText } from '@material-ui/core';
import clsx from 'clsx';
import path from 'path';
import {
  changeBasicAdjustments,
  changeCameraTransform,
  selectBasicAdjustments,
  selectCameraTransform,
  selectActiveCamera
} from '../redux/camera-slice';
import { saveProject } from '../project-utils';
import { selectAllAssets, getCameraFileAssets } from '../redux/assets-slice';
import { Viewer } from '../viewer/js/main';
import { pointObsColor, imageObsColor } from '../viewer/js/image-aligner/image-aligner';
import LocalScene from '../viewer/js/projections';

const highCutoff = 1.0;
const lowCutoff = 0.5;
const requiredRows = 5;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    minHeightContent: {
      minHeight: "100px"
    },
    toggleOptionParent: {
      margin: theme.spacing(0),
      padding: theme.spacing(0),
      boxShadow: "0px 10px 10px 2px",
      justifyContent: "center"
    },
    toggleOptionCheckbox: {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5)
    },
    toggleOptionFlex: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    userWarning: {
      padding: theme.spacing(3)
    },
    box: {
      padding: theme.spacing(4)
    },
    marginTop: {
      marginTop: theme.spacing(2)
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    adornedEnd: {
      paddingRight: theme.spacing(0)
    },
    utmButton: {
      marginLeft: theme.spacing(1)
    },
    selectMargin: {
      margin: theme.spacing(1),
    },
    button: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1)
    },
    dropdown: {
      minWidth: 200,
    },
    formControl: {
      width: '100%'
    },
    buttonDiv: {
      textAlign: "right"
    },
    panel: {
      background: "rgba(200,200,200,0.1)"
    },
    success: {
      color: theme.palette.success.main
    },
    imageObservationSuccess: {
      color: `#${imageObsColor.toString(16)}`
    },
    pointObservationSuccess: {
      color: `#${pointObsColor.toString(16)}`
    },
    warning: {
      color: theme.palette.warning.main
    },
    error: {
      color: theme.palette.error.main
    },
    info: {
      color: theme.palette.info.main
    },
    errorLow: {
      color: theme.palette.success.main
    },
    errorMedium: {
      color: theme.palette.warning.main
    },
    errorHigh: {
      color: theme.palette.error.main
    },
    assetCheckbox: {
      position: "relative",
      transform: "translateY(0%)"
    },
    checkboxPadding: {
      padding: "0px"
    }
  }),
);

const PointObservationIcon = (props) => {
  return ObservationIcon(props, 'point');
};

const ImageObservationIcon = (props) => {
  return ObservationIcon(props, 'image');
};


const ObservationIcon = (props, type) => {
  const {color} = props;
  const classes = useStyles();

  return (
    <IconButton
      size="small"
      classes={{
        colorPrimary: type === 'image' ? classes.imageObservationSuccess : classes.pointObservationSuccess,
        colorSecondary: classes.error,
        colorInherit: classes.info,
      }}
      color={color}
    >
      <ControlPointIcon fontSize="small"/>
    </IconButton>
  );
};

const ResidualError = (props) => {
  const {value} = props;
  const classes = useStyles();
  return (
    <Box className={clsx("", {
      [classes.errorLow]: (value < lowCutoff),
      [classes.errorMedium]: (value < highCutoff) && (value >= lowCutoff),
      [classes.errorHigh]: (value >= highCutoff)
    })}>
      {value}
    </Box>
  );
};

export const ImageAlignerAdvanced = (props) => {
  const viewer = props.viewer as Viewer;
  const {addObservations, setDefaultTab, setTabsEnabled,
    observationType, setObservationType, alignerAdvOpen,
    alignerBasicOpen, setAlignerAdvOpen} = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const showMessage = useSnackbar().enqueueSnackbar;

  const [rows, setRows] = useState([]);
  const [csvPath, setCsvPath] = useState("");
  const [encompassCsvPath, setEncompassCsvPath] = useState("");
  const [aligned, setAligned] = useState(false);
  const [alignmentConfirm, setAlignmentConfirm] = useState(false);
  const [alignerMatrixPath, setAlignerMatrixPath] = useState(null);
  const [valid, setValid] = useState(false);
  const [tableNonEmpty, setTableNonEmpty] = useState(false);
  const [closePrompt, setClosePrompt] = useState(false);
  const [isAligned, setIsAligned] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [askRemoveOpen, setAskRemoveOpen] = useState(false);
  const [alignPosition, setAlignPosition] = useState(true);
  const [alignRotation, setAlignRotation] = useState(true);

  const cameraTransformPath = useSelector(selectCameraTransform);
  const currentCamera = useSelector(selectActiveCamera);
  const basicAdjustments = useSelector(selectBasicAdjustments);

  const assets = useSelector(selectAllAssets);
  const camerasFiles = getCameraFileAssets(assets);

  const columns = [
    {id: 'obs_num', numeric: true, label: '#'},
    {id: 'name', numeric: false, label: 'Filename'},
    {id: 'img_obs', numeric: false, label: 'Image Control'},
    {id: 'pc_obs', numeric: false, label: 'Point Control'},
    {id: 'error', numeric: true, label: 'Error(\u00B0)'},
  ];

  const handleOpen = () => {
    if (alignerBasicOpen) {
      setAlertOpen(true);
      return;
    }

    setAlignerAdvOpen(true);
    setDefaultTab();
  };

  const handleClose = () => {
    setAlignerAdvOpen(false);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const resetRowsAndMesh = () => {
    setRows([]);
    generateRows(requiredRows);
  };

  const exportCSV = () => {
    let appPath = app.getPath('userData');
    let csvPath = appPath + '/alignTemp.csv';
    let csvOut = [];
    csvOut.push(['filename',
      'x_img', 'y_img', 'z_img',
      'x_ground', 'y_ground', 'z_ground']);
    for (let i=0; i<rows.length; i++) {
      let imageObs = viewer.getImageObsCameraAligner(i);
      let pointObs = viewer.getPointObsCameraAligner(i);
      csvOut.push([rows[i].name,
        imageObs.x, imageObs.y, imageObs.z,
        pointObs.x, pointObs.y, pointObs.z]);
    }
    csv.writeToPath(csvPath, csvOut)
      .on('error', err => console.error(err))
      .on('finish', () => console.log('Done writing.'));
    setCsvPath(csvPath);

    let csvEncompassPath = appPath + '/encompassTemp.csv';
    let csvEncompassOut = [];
    csvEncompassOut.push(['filename',
      'X', 'Y', 'Z',
      'Roll', 'Pitch', 'Yaw']);
    for (let i=0; i< viewer.getNumEntriesCameraAligner(); i++) {
      csvEncompassOut.push(viewer.getCamPoseCameraAligner(i));
    }
    csv.writeToPath(csvEncompassPath, csvEncompassOut)
      .on('error', err => console.error(err))
      .on('finish', () => console.log('Done writing.'));
    setEncompassCsvPath(csvEncompassPath);
  };

  const projectionUnits = LocalScene.dataProjectionUnits;
  const runAlignerPython =() => {
    let exe = new PythonExecutable();
    let commands = [
      "-p", "camera_aligner_advanced",
      "--csv_obs", csvPath,
      "--csv_encompass", encompassCsvPath,
      "--sulphur", "True",
      "--align_position", alignPosition ? "True" : "False",
      "--align_rotation", alignRotation ? "True" : "False",
      "--units", projectionUnits
    ];
    
    let isConverted = false;
    let camPoseEntry = viewer.getCamPoseCameraAligner(0);
    let largeResiduals = false;

    let initialPath = path.dirname(camPoseEntry[0]);
    let nameCorrection = path.basename(initialPath);
    let correctionName = nameCorrection + "_correction_matrix.4x4";
    let correctionPath = path.join(initialPath, correctionName);

    (async () => {
      new PythonExecutable().run({
        command: ['--check-tool', 'aligner-advanced']
      });
    })();

    exe.run({
      command: commands,
      saveLogs: true,
      onLine: jsonData => {
        let residualsTemp = parseResiduals(jsonData);
        if (residualsTemp.length !== 0) {
          viewer?.setErrorsCameraAligner(residualsTemp);
          isConverted = true;
        }
        residualsTemp.forEach(res => {
          if (res >= highCutoff) {
            largeResiduals = true;
          }
        });
      },
      onClose: () => {
        try {
          if (isConverted) {
            showMessage("Alignment Adjustment Complete!", {
              variant: "success"
            });
            setAlignerMatrixPath(correctionPath);
            setAligned(true);
            updateRows(resetRowIcons());
            if (largeResiduals) {
              showMessage("Large errors in alignment correction", {
                variant: "warning"
              });
            }
          } else {
            showMessage("Alignment Correction experienced a problem and did not apply a correction", {
              variant: "error"
            });
          }
        }
        catch (err) {
          showMessage("Alignment Correction experienced a problem and did not apply a correction", {
            variant: "error"
          });
        }
      }
    });
  };

  const parseResiduals = (jsonData) => {
    let resArray = [];

    if (jsonData) {
      jsonData.forEach(res => {
        let rounded = Math.round(parseFloat(res) * 100) / 100;
        resArray.push(rounded);
      });
    }

    return resArray;
  };

  const emptyObservation = (index) => {
    return {
      id: index,
      obs_num: index+1,
      name: "No File",
      img_obs: <ImageObservationIcon color="secondary"/>,
      pc_obs: <PointObservationIcon color="secondary"/>,
      error: 0
    };
  };

  const generateRows = (rowNum) => {
    viewer?.resetCameraAligner();

    let rowsTemp = [];
    for (let i=0; i<rowNum; i++) {
      rowsTemp.push(emptyObservation(i));
      viewer?.addRowCameraAligner();
    }
    updateRows(rowsTemp);
  };

  const addRows = (rowAdd) => {
    let tempRows = [...rows];

    for (let i=0; i<rowAdd; i++) {
      tempRows.push(emptyObservation(i));
      viewer.addRowCameraAligner();
    }

    updateRows(tempRows);
    showMessage("Aligner Entry Added", {
      variant: "success"
    });
    setAlignmentConfirm(false);
    if (aligned) {
      cancelCurrentAlignment();
      setValid(false);
    }
  };

  const updateRows = (tempRows) => {
    if (tempRows.length === 0) {
      return;
    }

    for (let i=0; i<tempRows.length; i++) {
      tempRows[i].id = i;
      tempRows[i].obs_num = i + 1;
      tempRows[i].name = viewer?.getCamName(i);
      tempRows[i].error = <ResidualError value={viewer?.getErrorCameraAligner(i)}/>;
    }

    setRows(tempRows);
  };

  const onDeleteClick = (row) => {
    if (rows.length === 1) {
      resetRowsAndMesh();
      return;
    }
    let tempRows = resetRowIcons();
    viewer.deleteRowCameraAligner(row.id);
    tempRows.splice(row.id, 1);
    updateRows(tempRows);
    setObservationType("none");
    showMessage("Aligner Entry Deleted", {
      variant: "error"
    });
    setAlignmentConfirm(false);
    if (aligned) {
      cancelCurrentAlignment();
      setValid(false);
    }
  };

  const resetRowIcons = () => {
    let tempRows = [...rows];

    for (let i=0; i<tempRows.length; i++) {
      const hasImageObs = viewer.getImageObsCameraAligner(i) != null;
      tempRows[i].img_obs = <ImageObservationIcon color={hasImageObs ? "primary" : "secondary"}/>;

      const hasPointObs = viewer.getPointObsCameraAligner(i) != null;
      tempRows[i].pc_obs = <PointObservationIcon color={hasPointObs ? "primary" : "secondary"}/>;
    }

    return tempRows;
  };

  const onRowClick = (row, index) => {
    if (index > 3 || index < 2){
      setObservationType("none");
      viewer.setCurrentRowCameraAligner(-1);
      let camID = viewer.getCamIdFromAligner(row.id);
      const position = viewer.getObservationPosition(row.id);
      viewer.setCameraVisible(camID, {position});
      viewer.flashObservations(row.id);
    } else if (index === 2){
      if (aligned || viewer?.checkIfAligned()) {
        cancelCurrentAlignment();
        setValid(false);
      }

      let tempRows = resetRowIcons();
      tempRows[row.id].img_obs = <ImageObservationIcon color="inherit" />;

      updateRows(tempRows);

      setObservationType("image");
      viewer.setCurrentRowCameraAligner(row.id);
      viewer.hideCurrentObservation('image', row.id);
      setAlignmentConfirm(false);
    } else if (index === 3) {
      if (aligned || viewer?.checkIfAligned()) {
        cancelCurrentAlignment();
        setValid(false);
      }

      let tempRows = resetRowIcons();

      tempRows[row.id].pc_obs = <PointObservationIcon color="inherit"/>;
      updateRows(tempRows);

      setObservationType("point");
      viewer.setCurrentRowCameraAligner(row.id);
      viewer.hideCurrentObservation('point', row.id);
      setAlignmentConfirm(false);
    }
  };

  const cancelCurrentAlignment = () => {
    setAligned(false);
    setIsAligned(false);
    setAlignmentConfirm(false);
    setAlignerMatrixPath(null);
    dispatch(changeCameraTransform(null));
    showMessage(`Alignment Cleared`, {
      variant: "success"
    });
  };

  const onClearAlignments = () => {
    dispatch(changeBasicAdjustments({}));
    viewer?.resetBasicAlignerCamera();
    saveProject(false);
    cancelCurrentAlignment();
  };

  const confirmAlignment = () => {
    dispatch(changeCameraTransform(alignerMatrixPath));
    setAlignmentConfirm(true);
  };

  const handleAlignPositionCheck = () => {
    setAlignPosition(!alignPosition);
  };

  const handleAlignRotationCheck = () => {
    setAlignRotation(!alignRotation);
  };

  const checkRowsValid = () => {
    if (rows.length < requiredRows) {
      setValid(false);
      setTableNonEmpty(false);
      return false;
    }

    if (!viewer.getNumEntriesCameraAligner()){
      setValid(false);
      return false;
    }

    if (viewer === null) {
      setValid(false);
      setTableNonEmpty(false);
      return false;
    }

    let validCheck = true;
    let nonEmpty = false;
    for (let i=0; i < rows.length; i++) {
      let imgCheck = (viewer?.getImageObsCameraAligner(i) != null);
      let pointCheck = (viewer?.getPointObsCameraAligner(i) != null);
      let validTemp = (imgCheck && pointCheck);
      validCheck = (validCheck && validTemp);
      nonEmpty = (imgCheck || pointCheck || nonEmpty);
    }
    setValid(validCheck);
    setTableNonEmpty(nonEmpty);
    return validCheck;
  };

  const checkClose = () => {
    if (tableNonEmpty) {
      setClosePrompt(true);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-image-aligner-adv");
    ipcRenderer?.on("open-image-aligner-adv", handleOpen);
  }, [alignerBasicOpen]);

  useEffect(() => {
    if (viewer) {
      generateRows(requiredRows);
    }
  }, [viewer]);

  useEffect(() => {
    const aligned = cameraTransformPath !== null;
    const removed = currentCamera === null;
    const noCameras = camerasFiles.length === 0;

    if (aligned && removed && noCameras) {
      setAskRemoveOpen(true);
    }
  }, [currentCamera]);

  useEffect(() => {
    if (addObservations) {
      showMessage(`New ${observationType} observation added`, {
        variant: "success"
      });
      updateRows(resetRowIcons());
      setObservationType("none");
      checkRowsValid();
    }
  }, [addObservations]);

  useEffect(() => {
    if (!viewer) {
      return;
    }

    viewer?.setObservationTypeCameraAligner(observationType);
    if (observationType==="none") {
      updateRows(resetRowIcons());
    }
  }, [viewer, observationType]);

  useEffect(() => {
    if (!alignerAdvOpen) {
      resetRowsAndMesh();
    }

    setTabsEnabled(!alignerAdvOpen);
    viewer?.toggleCameraAligner(alignerAdvOpen);
  }, [viewer, alignerAdvOpen]);

  useEffect(() => {
    let validCheck = checkRowsValid();
    if (validCheck) {
      exportCSV();
    }
  }, [rows]);

  useEffect(() => {
    setIsAligned(viewer?.checkIfAligned() || hasBasicAlignment);
  }, [viewer, alignerAdvOpen, aligned, isAligned]);

  useEffect(() => {
    if (alignerAdvOpen && !alignRotation && !alignPosition) {
      showMessage("At least one of Align Position and Align Rotation must be enabled", {
        variant: "warning"
      });
      setAlignPosition(true);
      setAlignRotation(true);
    }

  }, [alignPosition, alignRotation]);

  const hasBasicAlignment = Object.keys(basicAdjustments).length !== 0;

  return (
    <React.Fragment>

      <DraggableDialog
        open={alignerAdvOpen}
        title="Data Aligner (Advanced)"
        onClose={checkClose}
      >
        <DialogContent className={classes.minHeightContent}>

          {(!isAligned) && (<Typography variant="caption">
            Select at least ({requiredRows}) corresponding points in both image and point cloud to align data.
            More observations may help give a better final result.
          </Typography>)}

          {(isAligned) && (<Typography variant="caption" className={classes.warning}>
            Image and Point Cloud data currently have a set alignment. Current Alignment must be cleared
            before adding observations.
          </Typography>)}

          {(!isAligned) && <EnhancedTable
            columns={columns}
            rows={rows}
            className={classes.marginTop}
            rowType="control point"
            onRowClick={onRowClick}
            deletable={true}
            onDeleteClick={onDeleteClick}
            pageSize={10}
            dense={true}
            sortable={false}
            wordBreak={true}
          />}
        </DialogContent>

        <DialogActions>
          {rows.length === 0 && (
            <Button onClick= {() => generateRows(requiredRows)} color="primary">
              Select Control Points
            </Button>
          )}

          {(!isAligned) && (rows.length >= 1) &&
            ((alignmentConfirm === false)) && (
            <Button onClick= {() => addRows(1)} color="inherit" >
              Add Entry
            </Button>
          )}

          {(valid === true) && (aligned === false) && (
            <Button onClick={runAlignerPython} color="primary" >
              Run Alignment Correction
            </Button>
          )}

          {(aligned === true) && (alignmentConfirm === true) && (
            <Button onClick={()=>saveMatrixFile(cameraTransformPath, showMessage)} color="primary">
              Save Camera Correction Matrix File 
            </Button>
          )}

          {(aligned === true) && (
            <Button onClick={cancelCurrentAlignment} color="secondary">
              Cancel Alignment Correction
            </Button>
          )}

          {(aligned === false) && (isAligned === true) && (
            <Button onClick={onClearAlignments} color="secondary">
              Clear Alignment Correction
            </Button>
          )}

          {(aligned === true) && (alignmentConfirm === false) && (
            <Button onClick={confirmAlignment} color="primary">
              Apply Alignment Correction
            </Button>
          )}

        </DialogActions>

        {!isAligned && (<DialogActions className={classes.toggleOptionParent}>

          {/* Calculate lever arm */}
          <div className={classes.toggleOptionFlex}>
            <Typography variant="caption">
              Align Position
            </Typography>
            <Checkbox
              checked={alignPosition}
              onChange={handleAlignPositionCheck}
              classes={{root: classes.toggleOptionCheckbox}}
              size="small"
            />
          </div>

          {/* Calculate rotation */}
          <div className={classes.toggleOptionFlex}>
            <Typography variant="caption">
              Align Rotation
            </Typography>
            <Checkbox
              checked={alignRotation}
              onChange={handleAlignRotationCheck}
              classes={{root: classes.toggleOptionCheckbox}}
              size="small"
            />
          </div>

        </DialogActions>)}
      </DraggableDialog>

      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          handleClose();
          setClosePrompt(false);
        }}
        title="Close Aligner?"
        prompt={`Do you want to close the Data Aligner? All observations will be lost.`}
        button="Close"
      />

      <PromptDialog
        open={askRemoveOpen}
        onCancel={() => setAskRemoveOpen(false)}
        onSubmit={() => {
          dispatch(changeCameraTransform(null));
          setAskRemoveOpen(false);
        }}
        title="Remove Alignment Matrix?"
        prompt={`All cameras have been removed. Do you want to remove the current camera
          correction matrix? If the existing matrix is not removed it will automatically
          be applied to all new CSVs.`}
        button="Remove Matrix"
      />

      <AlertDialog
        open={alertOpen}
        title="Data Aligner"
        handleClose={handleAlertClose}
      >
        <DialogContentText>
          Unable to open advanced data aligner if basic mode is open
        </DialogContentText>
      </AlertDialog>
    </React.Fragment>
  );
};
